/**
 * Every documented error code, typed — not stringly-typed. `token_expired` is handled
 * transparently inside the client and should never reach a screen; every other code is a real
 * outcome a UI must render something for.
 */

export type ApiErrorCode =
  // Session grant
  | "token_invalid"
  | "token_expired"
  | "product_disabled"
  | "surface_not_enabled"
  | "venue_not_found"
  | "rate_limited"
  // Search
  | "search_disabled"
  | "query_too_short"
  // Requests
  | "requests_closed"
  | "player_offline"
  | "outside_geofence"
  | "position_required"
  | "track_unavailable"
  | "track_cooldown"
  | "requester_limit"
  | "venue_limit"
  // Fallback for anything the contract hasn't documented yet, or a transport failure.
  | "unknown"
  | "network_error";

export interface ApiErrorPayload {
  code: ApiErrorCode;
  status: number;
  message?: string;
}

/** Discriminate on `.code`, not `instanceof` subclasses — keeps call sites exhaustive-checkable. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(payload: ApiErrorPayload) {
    super(payload.message ?? payload.code);
    this.name = "ApiError";
    this.code = payload.code;
    this.status = payload.status;
  }
}

const KNOWN_CODES: ReadonlySet<string> = new Set<ApiErrorCode>([
  "token_invalid",
  "token_expired",
  "product_disabled",
  "surface_not_enabled",
  "venue_not_found",
  "rate_limited",
  "search_disabled",
  "query_too_short",
  "requests_closed",
  "player_offline",
  "outside_geofence",
  "position_required",
  "track_unavailable",
  "track_cooldown",
  "requester_limit",
  "venue_limit",
]);

/** Normalises a maestro-api error body into an `ApiError`. Unknown codes degrade to `"unknown"`. */
export function toApiError(status: number, body: unknown): ApiError {
  const rawCode = isRecord(body) && typeof body.code === "string" ? body.code : undefined;
  const message = isRecord(body) && typeof body.message === "string" ? body.message : undefined;
  const code: ApiErrorCode = rawCode && KNOWN_CODES.has(rawCode) ? (rawCode as ApiErrorCode) : "unknown";
  return new ApiError({ code, status, message });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
